import React, { useEffect, useRef, useState } from 'react'

const FeaturesSection = () => {
    const sectionRef = useRef(null);
    const [activeCategory, setActiveCategory] = useState('clay');

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll:not(.feature-card)');
        elements?.forEach((el, index) => {
            el.style.animationDelay = `${index * 0.1}s`;
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const categories = [
        { id: 'clay', name: 'Clay Shooting', icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <circle cx="12" cy="12" r="6"/>
                <circle cx="12" cy="12" r="2"/>
            </svg>
        )},
        { id: 'rifle', name: 'Rifle Shooting', icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12h6"/>
                <path d="M22 12h-4"/>
                <path d="M12 2v6"/>
                <path d="M12 22v-4"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="12" cy="12" r="8"/>
            </svg>
        )},
        { id: 'game', name: 'Game Shooting', icon: (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3v2"/>
                <path d="M19.07 4.93l-1.41 1.41"/>
                <path d="M21 12h-2"/>
                <path d="M3 12h2"/>
                <path d="M4.93 4.93l1.41 1.41"/>
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 21v-2"/>
                <path d="M12 17l3 4"/>
                <path d="M12 17l-3 4"/>
            </svg>
        )}
    ];

    const platformCoreFeatures = [
        {
            title: 'Marketplace',
            description: 'Buy and sell kit with venue and shooter listings side by side.'
        },
        {
            title: 'Connect Perks',
            description: 'Discover venue offers and claim with your Connect Pass QR.'
        },
        {
            title: 'Venues',
            description: 'Find clubs, ranges, and estates, then save favourites for quick access.'
        },
        {
            title: 'Profiles & Friends',
            description: 'Build your network, share progress, and connect with fellow shooters.'
        }
    ];

    const featuresByCategory = {
        clay: [
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="12" cy="12" r="6"/>
                        <circle cx="12" cy="12" r="2"/>
                    </svg>
                ),
                title: 'Score Tracking',
                description: 'Log every shot with precision. Track Sporting, Skeet, Trap, and Compak scores across all clay disciplines.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                ),
                title: 'Club Connect',
                description: 'Find clay grounds near you. Access member events, book stands, and connect with fellow clay shooters.',
                color: 'green'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                        <path d="M8 14h.01"/>
                        <path d="M12 14h.01"/>
                        <path d="M16 14h.01"/>
                    </svg>
                ),
                title: 'Competition Management',
                description: 'Enter competitions, view draws, and track live leaderboards. From club shoots to championship events.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3v18h18"/>
                        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
                    </svg>
                ),
                title: 'Performance Analytics',
                description: 'Deep dive into your shooting patterns. Analyze stand performance, identify weaknesses, and track improvement.',
                color: 'green'
            }
        ],
        rifle: [
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="12" cy="12" r="6"/>
                        <circle cx="12" cy="12" r="2"/>
                        <line x1="12" y1="2" x2="12" y2="6"/>
                        <line x1="12" y1="18" x2="12" y2="22"/>
                        <line x1="2" y1="12" x2="6" y2="12"/>
                        <line x1="18" y1="12" x2="22" y2="12"/>
                    </svg>
                ),
                title: 'Precision Scoring',
                description: 'Record scores for prone, standing, and kneeling positions. Support for 10m, 50m, and 300m distances.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12h6"/>
                        <path d="M22 12h-6"/>
                        <path d="M12 2v6"/>
                        <path d="M12 16v6"/>
                        <circle cx="12" cy="12" r="2"/>
                    </svg>
                ),
                title: 'Zero Tracking',
                description: 'Log your rifle zeros, scope adjustments, and ballistic data. Never lose your settings between sessions.',
                color: 'green'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                        <line x1="4" y1="22" x2="4" y2="15"/>
                    </svg>
                ),
                title: 'Range Finder',
                description: 'Discover approved rifle ranges. Check facilities, available distances, and book your lane in advance.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                ),
                title: 'Training Programs',
                description: 'Follow structured training plans. Track dry fire practice, live fire sessions, and conditioning work.',
                color: 'green'
            }
        ],
        game: [
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3v2"/>
                        <path d="M12 19v2"/>
                        <path d="M4.93 4.93l1.41 1.41"/>
                        <path d="M17.66 17.66l1.41 1.41"/>
                        <path d="M3 12h2"/>
                        <path d="M19 12h2"/>
                        <circle cx="12" cy="12" r="5"/>
                    </svg>
                ),
                title: 'Game Diary',
                description: 'Record your shoots - species, bag counts, locations, and conditions. Build your complete shooting history.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                    </svg>
                ),
                title: 'Estate Directory',
                description: 'Browse estates and syndicates. View driven days, walked-up shoots, and wildfowling locations.',
                color: 'green'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                ),
                title: 'Season Planner',
                description: 'Plan your season with shooting dates, beater days, and social events. Sync with your calendar.',
                color: 'orange'
            },
            {
                icon: (
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                ),
                title: 'Gun & Kit Management',
                description: 'Track your shotguns, rifles, and accessories. Log service history and ammunition preferences.',
                color: 'green'
            }
        ]
    };

    return (
        <section id="features" className="features-section" ref={sectionRef}>
            <div className="features-bg-gradient"></div>
            
            <div className="container features-content">
                {/* Section Header */}
                <div className="features-header animate-on-scroll">
                    <span className="features-eyebrow">FEATURES</span>
                    <h2 className="features-title">Tools for Every Discipline + Core Platform Features</h2>
                    <p className="features-subtitle">
                        Shoot Connect covers discipline-specific scoring and the wider platform experience:
                        marketplace, perks, profiles, friends, venue discovery, and a mobile-responsive experience across phone, tablet, and desktop.
                    </p>
                </div>

                <div className="platform-core-grid animate-on-scroll">
                    {platformCoreFeatures.map((feature) => (
                        <div className="platform-core-card" key={feature.title}>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </div>
                    ))}
                </div>

                {/* Category Tabs */}
                <div className="category-tabs animate-on-scroll">
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category.id)}
                        >
                            <span className="category-tab-icon">{category.icon}</span>
                            <span className="category-tab-name">{category.name}</span>
                        </button>
                    ))}
                </div>

                {/* Features Grid */}
                <div className="features-grid" key={activeCategory}>
                    {featuresByCategory[activeCategory].map((feature, index) => (
                        <div 
                            key={`${activeCategory}-${index}`} 
                            className={`feature-card feature-card-${feature.color}`}
                            style={{ animationDelay: `${index * 0.1}s` }}
                        >
                            <div className={`feature-icon feature-icon-${feature.color}`}>
                                {feature.icon}
                            </div>
                            <h3 className="feature-title">{feature.title}</h3>
                            <p className="feature-description">{feature.description}</p>
                            <button type="button" className="feature-link">
                                Learn more
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>

                {/* CTA Section */}
                <div className="features-cta animate-on-scroll">
                    <div className="features-cta-content">
                        <h3>Ready to improve your shooting?</h3>
                        <p>Create your free Shoot Connect account and start today.</p>
                    </div>
                    <a href="/register" className="default-btn">
                        Create Free Account
                    </a>
                </div>
            </div>
        </section>
    )
}

export default FeaturesSection
